import { theme as antdTheme } from "antd";
import {
  BRANDS,
  COMPONENT_TOKENS,
  DEFAULT_BRAND,
  MODE_TOKEN_OVERRIDES,
  STATUS_COLORS,
  THEME_MODES,
} from "./themeConfig";
import { ACCENT_TOKENS, SEMANTIC_TOKENS, cssVarName } from "./semanticTokens";

const algorithmFor = mode =>
  mode === THEME_MODES.DARK ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

const brandFor = brand => BRANDS[brand] || BRANDS[DEFAULT_BRAND];

/** The antd `ConfigProvider` theme for one (mode, brand). */
export const getAntdTheme = (mode, brand) => {
  const { primary } = brandFor(brand);
  return {
    algorithm: algorithmFor(mode),
    token: {
      colorPrimary: primary,
      // antd derives links from colorInfo (always blue); a brand owns its links.
      colorLink: primary,
      colorSuccess: STATUS_COLORS.success,
      colorWarning: STATUS_COLORS.warning,
      colorError: STATUS_COLORS.error,
      colorInfo: STATUS_COLORS.info,
      ...MODE_TOKEN_OVERRIDES[mode],
    },
    components: COMPONENT_TOKENS[mode],
  };
};

const pick = (token, map) =>
  Object.fromEntries(Object.entries(map).map(([name, aliasKey]) => [name, token[aliasKey]]));

/** Semantic token values ({ "bg-container": "#ffffff", accent: "#722ed1", … }) for one (mode, brand). */
export const getSemanticValues = (mode, brand) => {
  const base = getAntdTheme(mode, brand);
  const token = antdTheme.getDesignToken(base);
  // Same algorithm and mode overrides, accent seed in the primary slot.
  const accentToken = antdTheme.getDesignToken({
    ...base,
    token: { ...base.token, colorPrimary: brandFor(brand).accent },
  });
  return { ...pick(token, SEMANTIC_TOKENS), ...pick(accentToken, ACCENT_TOKENS) };
};

const declarations = values =>
  Object.entries(values)
    .map(([name, value]) => `${cssVarName(name)}:${value};`)
    .join("");

/**
 * The full stylesheet of semantic CSS variables for every (mode × brand).
 *
 *   :root                                   light + default brand (also the
 *                                           fallback for pages with no attributes)
 *   :root[data-theme="dark"]                dark neutrals + default brand
 *   :root[data-theme=…][data-brand="x"]     only the tokens brand "x" changes
 *
 * Emitting just the per-brand diff keeps the inline <style> small as brands grow.
 */
let cachedStylesheet = null;
export const buildThemeStylesheet = () => {
  if (cachedStylesheet) return cachedStylesheet;
  const rules = [THEME_MODES.LIGHT, THEME_MODES.DARK].flatMap(mode => {
    const base = getSemanticValues(mode, DEFAULT_BRAND);
    const modeSelector = mode === THEME_MODES.LIGHT ? ":root" : `:root[data-theme="${mode}"]`;
    const baseRule = `${modeSelector}{color-scheme:${mode};${declarations(base)}}`;
    const brandRules = Object.keys(BRANDS)
      .filter(brand => brand !== DEFAULT_BRAND)
      .map(brand => {
        const values = getSemanticValues(mode, brand);
        const diff = Object.fromEntries(
          Object.entries(values).filter(([name, value]) => base[name] !== value)
        );
        return `:root[data-theme="${mode}"][data-brand="${brand}"]{${declarations(diff)}}`;
      });
    return [baseRule, ...brandRules];
  });
  cachedStylesheet = rules.join("\n");
  return cachedStylesheet;
};
